import { Link } from 'react-router-dom';
import { ArrowUpRight, FolderHeart, Instagram, Mail, PenTool } from 'lucide-react';
import { BrandLogo } from './BrandLogo';

const footerLinks = {
  Product: [
    { label: 'Create invitation', href: '/create', internal: true },
    { label: 'Invitation library', href: '/library', internal: true },
    { label: 'Pricing', href: '/pricing', internal: true },
  ],
  Studio: [
    { label: 'Showcase', href: '/#showcase', internal: false },
    { label: 'Process', href: '/#process', internal: false },
    { label: 'App concept', href: '/#app-preview', internal: false },
  ],
  Support: [
    { label: 'Help center', href: '/help', internal: true },
    { label: 'Privacy policy', href: '/privacy', internal: true },
    { label: 'Terms of service', href: '/terms', internal: true },
    { label: 'Cookie policy', href: '/cookies', internal: true },
  ],
};

function FooterLink({ item }) {
  const className = "text-sm text-[#6C655E] transition hover:text-[#1F1A17] hover:underline underline-offset-2";
  if (item.internal) {
    return <Link to={item.href} className={className}>{item.label}</Link>;
  }
  return <a href={item.href} className={className}>{item.label}</a>;
}

export function Footer() {
  return (
    <footer
      className="border-t border-[#E8DED3] bg-[linear-gradient(180deg,#FFFDF9_0%,#F8F2EB_100%)] py-14 md:py-20"
      data-testid="footer"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-8 rounded-[36px] border border-[#E8DED3] bg-[rgba(255,252,247,0.85)] p-8 shadow-[0_28px_80px_rgba(66,50,33,0.06)] backdrop-blur-sm lg:grid-cols-[1.15fr_1fr] lg:p-10">

          {/* Brand column */}
          <div>
            <BrandLogo
              linked
              iconSize={36}
              titleClassName="text-2xl"
              subtitle="Crafted invitation studio"
              subtitleClassName="text-xs text-[#8C8377]"
            />
            <h3 className="mt-8 max-w-xl font-serif text-[2.1rem] leading-[1.02] tracking-[-0.04em] text-[#1F1A17] md:text-[2.8rem]">
              Turn every saved invitation into a cleaner, calmer planning experience.
            </h3>
            <p className="mt-5 max-w-xl text-sm leading-7 text-[#6C655E] md:text-base">
              Designed for couples who want premium invitation design without losing the warmth, softness, and emotional detail that make weddings feel personal.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/create"
                className="inline-flex items-center gap-2 rounded-full bg-[#1F1A17] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#0E0C0B] hover:scale-[1.02] active:scale-100"
              >
                <PenTool size={16} />
                Start creating
              </Link>
              <Link
                to="/library"
                className="inline-flex items-center gap-2 rounded-full border border-[#E3D7CA] bg-white px-5 py-3 text-sm font-medium text-[#1F1A17] transition hover:border-[#C7B49A] hover:scale-[1.02] active:scale-100"
              >
                <FolderHeart size={16} />
                View your library
              </Link>
            </div>
          </div>

          {/* Links columns */}
          <div className="grid gap-8 md:grid-cols-3">
            {Object.entries(footerLinks).map(([heading, items]) => (
              <div key={heading}>
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8A7A6A]">{heading}</div>
                <div className="mt-4 space-y-3">
                  {items.map((item) => (
                    <div key={item.label}>
                      <FooterLink item={item} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col gap-4 border-t border-[#E8DED3] pt-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#8B8379]">
            <span>© 2026 VowVisionsAI. Built for softer celebrations.</span>
            <Link to="/privacy" className="hover:text-[#1F1A17] hover:underline underline-offset-2">Privacy</Link>
            <Link to="/terms" className="hover:text-[#1F1A17] hover:underline underline-offset-2">Terms</Link>
            <Link to="/cookies" className="hover:text-[#1F1A17] hover:underline underline-offset-2">Cookies</Link>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="mailto:hello@vowvisionsai.com"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3D7CA] bg-white text-[#6C655E] transition hover:text-[#1F1A17] hover:border-[#C7B49A]"
              aria-label="Email us"
            >
              <Mail size={17} />
            </a>
            <a
              href="#"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3D7CA] bg-white text-[#6C655E] transition hover:text-[#1F1A17] hover:border-[#C7B49A]"
              aria-label="Follow on Instagram"
            >
              <Instagram size={17} />
            </a>
            <a
              href="/#top"
              className="inline-flex items-center gap-2 rounded-full border border-[#E3D7CA] bg-white px-4 py-2 text-sm text-[#1F1A17] transition hover:border-[#C7B49A] hover:scale-[1.02] active:scale-100"
            >
              Back to top <ArrowUpRight size={15} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
